import { MagnifyingGlassIcon, XIcon } from '@phosphor-icons/react';
import React, { useEffect, useRef, useState } from 'react';

import { CircleIconButton } from '@/components/ui/circle-icon-button';
import { Input } from '@/components/ui/input';
import { Spinner } from '@/components/ui/spinner';
import { cn } from '@/lib/utils';

interface SearchBarProps {
  value?: string;
  onChange?: (value: string) => void;
  onClear?: () => void;
  placeholder?: string;
  variant?: 'default' | 'modal' | 'compact' | 'minimal' | 'neutral';
  size?: 'sm' | 'md' | 'lg';
  autoFocus?: boolean;
  showClearButton?: boolean;
  loading?: boolean;
  disabled?: boolean;
  className?: string;
  onSubmit?: (value: string) => void;
  onFocus?: () => void;
  onBlur?: () => void;
  /** Render inside an existing form without creating invalid nested form markup. */
  embedded?: boolean;
}

const searchVariants = {
  default: {
    container: 'relative flex items-center',
    input: 'pl-10 pr-10 border-border/70',
    icon: 'absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground',
    clearButton: 'absolute right-2 top-1/2 -translate-y-1/2',
  },
  modal: {
    container: 'relative flex items-center w-full',
    // `dark:bg-muted/30` pairs the light-mode override explicitly. Without
    // it, `Input`'s own `dark:bg-input` (a DIFFERENT tailwind-merge group,
    // since it carries the `dark:` variant) survives the merge alongside
    // this bare `bg-muted/30` and the two fight over dark-mode
    // background-color by stylesheet source order — see the identical trap
    // this fixed on the `search-input` slot below.
    input: 'pl-10 pr-10 bg-muted/30 dark:bg-muted/30 border-0',
    icon: 'absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground',
    clearButton: 'absolute right-2 top-1/2 -translate-y-1/2',
  },
  compact: {
    container: 'relative flex items-center',
    input: 'pl-8 pr-8 h-8 border-border/70',
    icon: 'absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground size-4',
    clearButton: 'absolute right-1 top-1/2 -translate-y-1/2',
  },
  minimal: {
    container: 'relative flex items-center',
    // `dark:bg-transparent` — see the `neutral` variant's note below; both
    // want to stay transparent in dark mode too, not pick up `Input`'s
    // `dark:bg-input`.
    input: 'pl-8 pr-2 border-0 bg-transparent dark:bg-transparent',
    icon: 'absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground size-4',
    clearButton: 'absolute right-1 top-1/2 -translate-y-1/2',
  },
  neutral: {
    container: 'relative flex items-center',
    // `bg-transparent border-border/60` hoisted up from two call-site overrides
    // (`table-toolbar.tsx`, `PaidCampaignsPage.tsx`) that both existed only to
    // undo this variant's old `bg-muted/50 border-border/40` — every other
    // DataShell toolbar (`/discover`, `/discover/ads`, `/assets`, Documents,
    // Plans, Products) never applied that override, so `/campaigns` search was
    // transparent while everywhere else was filled. One skin for `neutral`.
    //
    // `dark:bg-transparent` pairs the override explicitly: `Input`'s own
    // `dark:bg-input` is a separate tailwind-merge group (different variant)
    // from a bare `bg-transparent`, so without this pairing the two fight
    // over dark-mode background-color by stylesheet source order instead of
    // tailwind-merge's guaranteed last-one-wins resolution.
    input: 'pl-8 pr-8 h-8 bg-transparent dark:bg-transparent border-border/60 hover:border-border',
    icon: 'absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground size-3.5',
    clearButton: 'absolute right-1 top-1/2 -translate-y-1/2',
  },
} as const;

const sizeConfig = {
  // `sm` (now the DEFAULT) — DataShell toolbars + every other search bar.
  // `h-9 md:h-8` keeps mobile at 36px (touch-target compliant) while desktop
  // gets the compact 32px treatment. Text MUST use `!text-caption` (and the
  // md:!text-caption duplicate) to override the wrapped `<Input>` cva's
  // `text-base md:text-sm` defaults — otherwise desktop sees `text-sm` (14px)
  // and reads bigger than the table-row titles.
  sm: {
    input: 'h-9 md:h-8 !text-caption md:!text-caption',
    icon: 'size-3.5',
    button: 'size-6',
  },
  md: {
    input: 'h-10 md:h-9 !text-caption md:!text-caption',
    icon: 'size-4',
    button: 'size-7',
  },
  lg: { input: 'h-11 md:h-10 !text-sm', icon: 'size-5', button: 'size-8' },
} as const;

// Deliberately no local focus-override class here anymore. This used to define
// `flatSearchFocusClasses` (border-only, `ring-0`) and apply it to every
// SearchBar, which meant the app's most common text field had NO focus ring
// while every button/pill beside it does. Removed so SearchBar inherits the
// `Input` primitive's own focus-visible treatment like every other text
// field — one focus treatment per control class. Do not reintroduce a
// bespoke focus class here.

export const SearchBar: React.FC<SearchBarProps> = ({
  value = '',
  onChange,
  onClear,
  placeholder = 'Search...',
  variant = 'default',
  // Default size flipped from 'md' → 'sm' so every uninstrumented caller
  // (Help pages, Studio MediaLibrary, etc.) inherits the compact rhythm.
  // Larger surfaces can still opt into 'md' or 'lg' explicitly.
  size = 'sm',
  autoFocus = false,
  showClearButton = true,
  loading = false,
  disabled = false,
  className,
  onSubmit,
  onFocus,
  onBlur,
  embedded = false,
}) => {
  const [internalValue, setInternalValue] = useState(value);
  const inputRef = useRef<HTMLInputElement>(null);
  const variantConfig = searchVariants[variant];
  const sizeStyles = sizeConfig[size];

  // Sync internal value with prop value
  useEffect(() => {
    setInternalValue(value);
  }, [value]);

  // Auto focus if requested
  useEffect(() => {
    if (autoFocus && inputRef.current) {
      inputRef.current.focus();
    }
  }, [autoFocus]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setInternalValue(newValue);
    onChange?.(newValue);
  };

  const handleClear = () => {
    setInternalValue('');
    onChange?.('');
    onClear?.();
    inputRef.current?.focus();
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit?.(internalValue);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      onSubmit?.(internalValue);
    }
    if (e.key === 'Escape') {
      handleClear();
    }
  };

  const showClear = showClearButton && internalValue.length > 0 && !loading;

  const content = (
    <div className="w-full" role={embedded ? 'search' : undefined}>
      <div className={cn(variantConfig.container, className)}>
        <MagnifyingGlassIcon className={cn(variantConfig.icon, sizeStyles.icon)} />
        <Input
          ref={inputRef}
          type="text"
          // Opts this field OUT of the dark-mode `background-color: … !important`
          // rule in index.css that forces every input to `--input`. Without it
          // the variant's `bg-transparent` is a no-op in dark mode and the
          // search field is the only filled object in its toolbar row. See that
          // rule's comment for the general trap.
          data-slot="search-input"
          value={internalValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={onFocus}
          onBlur={onBlur}
          placeholder={placeholder}
          disabled={disabled || loading}
          className={cn(variantConfig.input, sizeStyles.input, 'transition-colors duration-200')}
          aria-label={placeholder}
        />
        {showClear && (
          <CircleIconButton
            onClick={handleClear}
            ariaLabel="Clear search"
            className={cn(
              variantConfig.clearButton,
              sizeStyles.button,
              'hover:bg-muted/10 hover:text-foreground',
            )}
          >
            <XIcon className="size-3" weight="bold" />
          </CircleIconButton>
        )}
        {loading && (
          <div
            className={cn(
              variantConfig.clearButton,
              sizeStyles.button,
              'flex items-center justify-center',
            )}
          >
            <Spinner size="sm" />
          </div>
        )}
      </div>
    </div>
  );

  if (embedded) return content;

  return <form onSubmit={handleSubmit}>{content}</form>;
};

export default SearchBar;
